import { createContext, useContext, useEffect, useState } from "react";

export type POSSelection = {
  branchId: string;
  branchName: string;
  deviceId: string;
  deviceName: string;
  salespersonId?: string;
  salespersonName?: string;
  receiptPrinterId?: string;
  receiptPrinterName?: string;
  kitchenPrinterId?: string;
  kitchenPrinterName?: string;
  tableId?: string;
  tableName?: string;
  selectedAt: string;
};

type POSContextType = {
  selection: POSSelection | null;
  setSelection: (selection: POSSelection | null) => void;
  clearSelection: () => void;
};

const POSContext = createContext<POSContextType | undefined>(undefined);

const STORAGE_KEY = "posSelection";

export function POSProvider({ children }: { children: React.ReactNode }) {
  const [selection, setSelectionState] = useState<POSSelection | null>(null);

  useEffect(() => {
    // Load from localStorage on mount
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as POSSelection;
        setSelectionState(parsed);
      } catch {
        // Invalid data, clear it
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  const setSelection = (newSelection: POSSelection | null) => {
    setSelectionState(newSelection);
    if (newSelection) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSelection));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const clearSelection = () => {
    setSelectionState(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <POSContext.Provider value={{ selection, setSelection, clearSelection }}>
      {children}
    </POSContext.Provider>
  );
}

export function usePOSContext() {
  const context = useContext(POSContext);
  if (context === undefined) {
    throw new Error("usePOSContext must be used within a POSProvider");
  }
  return context;
}
